/**
 * 📝 SISTEMA DE LOGGING PROFESIONAL
 * Reemplazo centralizado para console.log/warn/error
 * 
 * Comportamiento:
 * - En desarrollo: muestra logs en consola con colores
 * - En producción: silenciado o envía a servicio de monitoreo
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  showTimestamp: boolean;
}

class Logger {
  private config: LoggerConfig;
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = import.meta.env?.MODE === 'development' || 
                         import.meta.env?.DEV === true ||
                         process.env.NODE_ENV === 'development';
    
    this.config = {
      enabled: this.isDevelopment,
      level: this.isDevelopment ? 'debug' : 'error',
      showTimestamp: true,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.config.enabled) return false;
    
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = levels.indexOf(this.config.level);
    const requestedLevelIndex = levels.indexOf(level);
    
    return requestedLevelIndex >= currentLevelIndex;
  }

  private formatMessage(level: LogLevel, args: unknown[]): void {
    if (!this.shouldLog(level)) return;

    const timestamp = this.config.showTimestamp 
      ? `[${new Date().toLocaleTimeString()}]` 
      : '';
    
    const prefix = timestamp ? `${timestamp} ` : '';

    switch (level) {
      case 'debug':
        console.log(`${prefix}🔍`, ...args);
        break;
      case 'info':
        console.info(`${prefix}ℹ️`, ...args);
        break;
      case 'warn':
        console.warn(`${prefix}⚠️`, ...args);
        break;
      case 'error':
        console.error(`${prefix}❌`, ...args);
        break;
    }
  }

  /**
   * Logging de debugging (solo en desarrollo)
   */
  debug(...args: unknown[]): void {
    this.formatMessage('debug', args);
  }

  /**
   * Logging informativo
   */
  info(...args: unknown[]): void {
    this.formatMessage('info', args);
  }

  /**
   * Advertencias importantes
   */
  warn(...args: unknown[]): void {
    this.formatMessage('warn', args);
  }

  /**
   * Errores críticos (siempre se muestran)
   */
  error(...args: unknown[]): void {
    // Los errores siempre se registran, incluso en producción
    const timestamp = this.config.showTimestamp 
      ? `[${new Date().toLocaleTimeString()}]` 
      : '';
    console.error(`${timestamp} ❌`, ...args);
  }

  /**
   * Logging condicional basado en una condición
   */
  debugIf(condition: boolean, ...args: unknown[]): void {
    if (condition) {
      this.debug(...args);
    }
  }

  /**
   * Logging de grupo
   */
  group(label: string, collapsed = false): void {
    if (!this.shouldLog('debug')) return;
    
    if (collapsed) {
      console.groupCollapsed(label);
    } else {
      console.group(label);
    }
  }

  groupEnd(): void {
    if (!this.shouldLog('debug')) return;
    console.groupEnd();
  }

  /**
   * Logging de tabla (útil para arrays de objetos)
   */
  table(data: unknown): void {
    if (!this.shouldLog('debug')) return;
    console.table(data);
  }

  /**
   * Timing de operaciones
   */
  time(label: string): void {
    if (!this.shouldLog('debug')) return;
    console.time(label);
  }

  timeEnd(label: string): void {
    if (!this.shouldLog('debug')) return;
    console.timeEnd(label);
  }

  /**
   * Configurar el logger en tiempo de ejecución
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

// Exportar instancia singleton
export const logger = new Logger();

// Función de conveniencia para configurar el logger globalmente
export function setupLogger(enabled: boolean, level?: LogLevel, showTimestamp?: boolean): void {
  logger.configure({
    enabled: enabled,
    level: level,
    showTimestamp: showTimestamp,
  });
}

// Exportar también como default para imports flexibles
export default logger;
